import { onValue, ref, remove } from "firebase/database";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";

import realtimeDB from "../helpers/firebase";
import * as SBService from "../helpers/sportbookService";
import { pingChallengeDeclined } from "../helpers/swissChallengePing";
import { swissRefreshState, userState } from "../states/common";
import "../pages/league/shared/league.css";

/**
 * Lời thách đấu Swiss: người kia vừa quét mã QR của bạn.
 *
 * Kênh realtime là Firebase, do CHÍNH máy người quét ghi vào ngay sau khi API
 * nhận lời mời (xem scanQR.tsx) — backend không nói chuyện với Firebase. Vì thế
 * kênh này chỉ là đường báo NHANH, không phải nguồn sự thật: máy người quét mất
 * mạng, hoặc người nhận đang đóng app, thì tin này rơi mất.
 *
 * Nguồn sự thật là league_matches.challengeInitiatorId. Đóng hộp thoại này không
 * mất lời mời — nó vẫn nằm ở tab Thi đấu của giải cho tới khi được xác nhận.
 *
 * Dùng đường riêng swiss-challenges/ chứ không chung notifications/ với
 * GlobalNotification: hộp thoại kia là của thể thức bắt kèo tự do, hình dạng dữ
 * liệu và luồng xử lý khác hẳn.
 */

type Invite = {
  leagueId: string;
  leagueName: string;
  challengerId: string;
  challengerName: string;
  round: string;
};

const SwissChallengeNotification = () => {
  const user = useRecoilValue(userState);
  const navigate = useNavigate();
  /* Trang giải có thể đang mở sẵn phía sau hộp thoại này. Điều hướng sang chính nó
     không dựng lại nó, nên phải báo bằng một tín hiệu riêng. */
  const nhoNapLai = useSetRecoilState(swissRefreshState);
  const [invite, setInvite] = useState<Invite | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // user luôn truthy (object mặc định có id rỗng) nên phải xét user.id: thiếu id
    // thì đường lắng nghe thành node gốc của mọi người dùng.
    if (!user?.id) {
      return;
    }
    const inviteRef = ref(realtimeDB, `swiss-challenges/${user.id}`);
    const unsubscribe = onValue(inviteRef, async (snapshot) => {
      const value = snapshot.val();
      if (!snapshot.exists() || !value?.leagueId) {
        return;
      }
      // Bị từ chối: chỉ cần một dòng thông báo, không có gì để bấm.
      if (value.type === "declined") {
        toast(`${value.declinedByName || "Đối thủ"} đã từ chối lời thách đấu`, { icon: "🚫" });
        await remove(inviteRef).catch(() => undefined);

        return;
      }
      if (!value.challengerId) {
        return;
      }
      setInvite({
        leagueId: String(value.leagueId),
        leagueName: String(value.leagueName || "giải đang tham gia"),
        challengerId: String(value.challengerId),
        challengerName: String(value.challengerName || "Đối thủ của bạn"),
        round: String(value.round || ""),
      });
      // Đọc xong thì dọn, để lần sau quét lại vẫn nổ được onValue.
      await remove(inviteRef).catch(() => undefined);
    });

    return () => unsubscribe();
  }, [user?.id]);

  const accept = async () => {
    if (!invite || busy) return;
    setBusy(true);
    try {
      await SBService.swissChallenge(invite.leagueId, invite.challengerId);
      toast.success("Đã xác nhận, mời nhập kết quả sau khi đánh xong");
      setInvite(null);
      nhoNapLai((n) => n + 1);
      navigate(`/league/${invite.leagueId}`);
    } catch (e: any) {
      const code = e?.response?.data?.error;
      const messages: Record<string, string> = {
        notPairedThisRound: "Người này không phải đối thủ của bạn ở vòng hiện tại",
        matchAlreadyFinished: "Trận này đã có kết quả",
        feeNotPaid: "Cần được xác nhận đóng phí trước khi thi đấu",
      };
      toast.error(messages[code] || "Không xác nhận được, vui lòng thử lại");
    } finally {
      setBusy(false);
    }
  };

  const decline = async () => {
    if (!invite || busy) return;
    setBusy(true);
    try {
      await SBService.swissDeclineChallenge(invite.leagueId, invite.challengerId);
      await pingChallengeDeclined(invite.challengerId, invite.leagueId, user?.name || "");
      toast.success("Đã từ chối lời thách đấu");
      setInvite(null);
      nhoNapLai((n) => n + 1);
    } catch (e: any) {
      const code = e?.response?.data?.error;
      const messages: Record<string, string> = {
        noChallengeToDecline: "Lời mời này không còn nữa",
        matchAlreadyStarted: "Trận đã bắt đầu, không từ chối được nữa",
      };
      toast.error(messages[code] || "Không từ chối được, vui lòng thử lại");
    } finally {
      setBusy(false);
    }
  };

  /* Đóng hộp thoại cũng phải làm mới. Lời mời đã nằm trong database từ lúc người kia
     quét mã - hộp thoại này chỉ là đường báo nhanh - nên tab Thi đấu phía sau đang
     hiện trạng thái cũ, và người dùng vừa biết có lời mời thì việc đầu tiên họ làm
     là nhìn xuống tab đó. */
  const dong = () => {
    setInvite(null);
    nhoNapLai((n) => n + 1);
  };

  if (!invite) {
    return null;
  }

  return (
    <div className="biadi">
      <div className="modalwrap">
        <div className="modalbd" onClick={dong} />
        <div className="modal">
          <div className="label" style={{ marginBottom: 10 }}>
            Lời thách đấu
          </div>
          <div className="callout">
            <div style={{ fontWeight: 600, marginBottom: 6 }}>{invite.challengerName}</div>
            <div className="small">
              vừa quét mã QR của bạn tại {invite.leagueName}
              {invite.round ? ` • ${invite.round}` : ""}.
            </div>
            <div className="small" style={{ marginTop: 8, color: "#E9CFC9" }}>
              Xác nhận để bắt đầu trận. Chỉ sau khi xác nhận, hai bên mới nhập được tỉ số.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <button className="btn btn-primary" style={{ flex: 1 }} disabled={busy} onClick={accept}>
              Xác nhận thi đấu
            </button>
            <button className="btn btn-ghost" style={{ flex: 1 }} disabled={busy} onClick={decline}>
              Từ chối
            </button>
          </div>
          <button
            className="btn btn-quiet btn-block"
            style={{ marginTop: 8 }}
            onClick={dong}
          >
            Để sau
          </button>
          <div className="tiny" style={{ marginTop: 10, textAlign: "center" }}>
            Bấm "Để sau" thì lời mời vẫn nằm ở tab Thi đấu của giải.
          </div>
        </div>
      </div>
    </div>
  );
};

export default SwissChallengeNotification;
